import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sprint 10.7 — Parent Dashboard at /parent/:registration_id.
// Auth: last 4 digits of the WhatsApp number used at registration.
// Distinct from the legacy auth-based /parent dashboard (ParentDashboard.js).
export default function ParentRegDashboard() {
  const { registration_id: regId } = useParams();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const submitPin = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{4}$/.test(pin)) {
      setError('Please enter the last 4 digits of your WhatsApp number.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/parent/${regId}/dashboard`, { pin });
      setData(r.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) setError('That PIN doesn\u2019t match. Try the last 4 digits of your WhatsApp number.');
      else if (status === 404) setError('We couldn\u2019t find this registration. Check the link you tapped.');
      else if (status === 429) setError(typeof detail === 'string' ? detail : 'Too many attempts. Try again in 15 minutes.');
      else setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4"
        data-testid="parent-pin-page"
      >
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="text-2xl" data-testid="parent-pin-title">
              Parent dashboard
            </CardTitle>
            <CardDescription>
              Enter the <strong>last 4 digits</strong> of the WhatsApp number
              you used to register.
            </CardDescription>
          </CardHeader>
          <form onSubmit={submitPin}>
            <CardContent className="space-y-4">
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{4}"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="4-digit PIN"
                className="text-center text-2xl tracking-[0.75em] font-mono"
                data-testid="parent-pin-input"
                autoFocus
              />
              {error && (
                <p className="text-sm text-red-600" data-testid="parent-pin-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={loading || pin.length !== 4}
                data-testid="parent-pin-submit"
              >
                {loading ? 'Checking\u2026' : 'Unlock dashboard'}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                Registration:{' '}
                <span className="font-mono" data-testid="parent-reg-id">
                  {regId}
                </span>
              </p>
            </CardContent>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="parent-dashboard">
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold" data-testid="parent-hello">
              Hello, {data.parent_name || 'parent'}
            </h1>
            <p className="text-slate-600 mt-1" data-testid="parent-child-line">
              Learner:{' '}
              <span className="font-semibold text-slate-900">{data.student_name}</span>{' '}
              &middot; Grade {data.grade} &middot; {data.school}
            </p>
          </div>
          <StatusBadge status={data.status} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card data-testid="parent-subscription-card">
            <CardHeader>
              <CardTitle className="text-lg">Subscription</CardTitle>
              <CardDescription>Your current access window</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-slate-700 space-y-1">
              <p>
                <span className="font-semibold">Status:</span>{' '}
                <span data-testid="parent-status-text">
                  {humanStatus(data.status)}
                </span>
              </p>
              {data.paid_until && (
                <p data-testid="parent-paid-until">
                  <span className="font-semibold">Paid until:</span>{' '}
                  {formatDate(data.paid_until)}
                </p>
              )}
              {data.provisional_expires_at && (
                <p data-testid="parent-provisional">
                  <span className="font-semibold">Provisional window ends:</span>{' '}
                  {formatDate(data.provisional_expires_at)}
                </p>
              )}
              <p className="pt-2">
                <span className="font-semibold">Registered:</span>{' '}
                {formatDate(data.created_at)}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="parent-account-card">
            <CardHeader>
              <CardTitle className="text-lg">Account</CardTitle>
              <CardDescription>Your registration details</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-slate-700 space-y-1">
              <p>
                <span className="font-semibold">WhatsApp:</span>{' '}
                <span className="font-mono" data-testid="parent-wa-masked">
                  {data.whatsapp_masked}
                </span>
              </p>
              <p>
                <span className="font-semibold">District:</span> {data.district}
              </p>
              <p>
                <span className="font-semibold">Language:</span> {data.language}
              </p>
              <p>
                <span className="font-semibold">Contact preference:</span>{' '}
                {data.preferred_contact}
              </p>
              <p className="pt-2 text-xs text-slate-500 font-mono">
                Reg id: {data.registration_id}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card data-testid="parent-receipts-card">
          <CardHeader>
            <CardTitle className="text-lg">Payment history</CardTitle>
            <CardDescription>
              Bank slips you&rsquo;ve submitted, most recent first
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(!data.receipts || data.receipts.length === 0) ? (
              <p className="text-sm text-slate-500" data-testid="parent-receipts-empty">
                No payment receipts yet.
              </p>
            ) : (
              <div className="space-y-2">
                {data.receipts.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white"
                    data-testid={`parent-receipt-${r.id}`}
                  >
                    <div className="text-sm">
                      <p className="font-semibold">{r.plan || 'Plan'}</p>
                      <p className="text-xs text-slate-500">
                        Submitted:{' '}
                        {r.paid_at ? formatDate(r.paid_at) : '\u2014'}
                      </p>
                      {r.note && <p className="text-xs text-slate-500 mt-1">Note: {r.note}</p>}
                    </div>
                    <ReceiptStatusBadge status={r.status} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-emerald-600 text-white" data-testid="parent-help-card">
          <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold">Need help?</h3>
              <p className="text-emerald-100 text-sm">
                Message the support team on WhatsApp for anything payment or account related.
              </p>
            </div>
            <Button
              asChild
              className="bg-white text-emerald-700 hover:bg-emerald-50"
              data-testid="parent-support-cta"
            >
              <a
                href="https://wa.me/94763845556"
                target="_blank"
                rel="noopener noreferrer"
              >
                Chat on WhatsApp
              </a>
            </Button>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-slate-400 pt-4">
          <Link to="/" className="hover:underline">
            &larr; Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- //
function humanStatus(s) {
  const map = {
    active: 'Active',
    active_free_october: 'Free October (active)',
    provisional_active: 'Provisional access (48h grace)',
    provisional_pending: 'Payment under review',
    lapsed: 'Lapsed \u2014 renew to continue',
    pending_bank_transfer: 'Awaiting bank transfer',
  };
  return map[s] || s || '\u2014';
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
    active_free_october: 'bg-sky-100 text-sky-800 border border-sky-300',
    provisional_active: 'bg-amber-100 text-amber-900 border border-amber-300',
    provisional_pending: 'bg-amber-50 text-amber-800 border border-amber-200',
    lapsed: 'bg-red-100 text-red-800 border border-red-300',
    pending_bank_transfer: 'bg-slate-100 text-slate-800 border border-slate-300',
  };
  return (
    <Badge
      className={map[status] || 'bg-slate-100 text-slate-800 border border-slate-300'}
      data-testid="parent-status-badge"
    >
      {humanStatus(status)}
    </Badge>
  );
}

function ReceiptStatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  let cls = 'bg-slate-100 text-slate-800 border border-slate-300';
  if (s === 'approved') cls = 'bg-emerald-100 text-emerald-800 border border-emerald-300';
  else if (s === 'rejected') cls = 'bg-red-100 text-red-800 border border-red-300';
  else if (s === 'pending' || s === 'submitted') cls = 'bg-amber-100 text-amber-900 border border-amber-300';
  return <Badge className={cls}>{status || 'pending'}</Badge>;
}

function formatDate(iso) {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch (_e) {
    return iso;
  }
}
